import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heading,
  Main,
  SectionLeft,
  SectionRight,
  SuggestionsList,
  SuggestionItem,
  SearchContainer,
  ErrorMessage,
  Wrapper,
  SubLabel,
} from './addressSearch.style';
import { Button, InputField } from '../../../components/uielements';
import useDebounce from '../../../hooks/useDebounce';
import { getAddressesList } from '../../../services/getAddressList';
import { getZoneDetails } from '../../../services/getSummary';
import backgroundImg from '../../../assets/PNG/address-search-page.png';
import backgroundImgCompress from '../../../assets/PNG/address-search-page-compress.png';
import useImageLoader from '../../../hooks/useImageLoader';
import { IntlMessages } from '../../../helper/langHelper';

const AdminAddressSearch = () => {
  const [formState, setFormState] = useState({
    addressInfo: null,
    clickedAddress: '',
    isAddressFind: false,
    isSuggestionClicked: false,
    activeIndex: -1,
  });

  const [zoneDetailsData, setZoneDetailsData] = useState(null);
  const [isLoadingZoneDetails, setIsLoadingZoneDetails] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const addressSearchBg = useImageLoader(backgroundImgCompress, backgroundImg);

  const {
    searchTerm,
    setSearchTerm,
    results: suggestions,
    isLoading,
    clearResults,
    isAPICalled,
  } = useDebounce(getAddressesList, 300);

  // Fetch zone details for selected address
  const fetchZoneDetails = async (selectedAddress) => {
    try {
      setIsLoadingZoneDetails(true);
      setError('');
      const payload = { propertyAddress: selectedAddress?.address };
      const response = await getZoneDetails(payload);
      setZoneDetailsData(response);
      return response;
    } catch (error) {
      setError('Failed to load zone details. Please try again later.');
      console.error('Error fetching zone details:', error);
      return null;
    } finally {
      setIsLoadingZoneDetails(false);
    }
  };

  // Address search input handler
  const handleAddressInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setFormState((prev) => ({
      ...prev,
      isSuggestionClicked: false,
      activeIndex: -1,
      clickedAddress: '',
      isAddressFind: false,
      addressInfo: null,
    }));
    setZoneDetailsData(null);
    setError('');
  };

  // Suggestion click handler - fetch zone details
  const handleSuggestionClick = async (selectedAddress) => {
    setFormState((prev) => ({
      ...prev,
      addressInfo: selectedAddress,
      clickedAddress: selectedAddress?.address || '',
      isAddressFind: true,
      isSuggestionClicked: true,
    }));
    clearResults();

    // Fetch zone details for the selected address
    await fetchZoneDetails(selectedAddress);
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (event) => {
    if (!suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFormState((prev) => ({
        ...prev,
        activeIndex:
          prev.activeIndex < suggestions.length - 1 ? prev.activeIndex + 1 : prev.activeIndex,
      }));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFormState((prev) => ({
        ...prev,
        activeIndex: prev.activeIndex > 0 ? prev.activeIndex - 1 : prev.activeIndex,
      }));
    } else if (event.key === 'Enter' && formState.activeIndex >= 0) {
      event.preventDefault();
      handleSuggestionClick(suggestions[formState.activeIndex]);
    } else if (event.key === 'Escape') {
      clearResults();
      setFormState((prev) => ({ ...prev, activeIndex: -1 }));
    }
  };

  // Form submission handler
  const handleSubmit = () => {
    if (!zoneDetailsData) {
      setError('Zone details are still loading. Please wait a moment.');
      return;
    }

    navigate(`/mapper/mapview`, {
      state: {
        searchAddress: formState.addressInfo,
        zoneDetailsData: zoneDetailsData,
      },
    });
  };

  // Form validation
  const isFormValid = () => {
    return formState.isSuggestionClicked && formState.addressInfo && zoneDetailsData;
  };

  return (
    <Main role="main">
      <SectionLeft>
        <Heading role="heading" aria-level="1">
          <IntlMessages id="address.search.header" />
        </Heading>
        <Wrapper>
          {/* Error Messages */}
          {error && (
            <ErrorMessage role="alert" aria-live="assertive">
              <span aria-hidden="true">⚠️</span> {error}
            </ErrorMessage>
          )}

          {/* Address Search Input */}
          <SubLabel role="heading" aria-level="2">
            <IntlMessages
              id="address.search.Qns"
              defaultMessage="What is the property address of your enquiry?"
            />
          </SubLabel>
          <SearchContainer role="search">
            <IntlMessages id="input.search.placeholder" defaultMessage="Enter your address here">
              {(placeholder) => (
                <InputField
                  type="text"
                  placeholder={placeholder}
                  value={formState.clickedAddress || searchTerm}
                  onChange={handleAddressInputChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="street-address"
                  aria-label="Search for your address"
                  aria-describedby="address-hint"
                  aria-expanded={suggestions.length > 0}
                  aria-controls="address-suggestions"
                  aria-activedescendant={
                    formState.activeIndex >= 0 ? `suggestion-${formState.activeIndex}` : undefined
                  }
                  disabled={isLoadingZoneDetails}
                />
              )}
            </IntlMessages>

            <span id="address-hint" className="visually-hidden">
              Start typing to search for your address
            </span>
            <SuggestionsList
              id="address-suggestions"
              role="listbox"
              aria-label="Address suggestions"
            >
              {isLoading ? (
                <SuggestionItem role="status" aria-live="polite">
                  <IntlMessages id="loading.suggestions" defaultMessage="Loading suggestions..." />
                </SuggestionItem>
              ) : (
                suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={`suggestion-${index}`}
                    id={`suggestion-${index}`}
                    role="option"
                    aria-selected={index === formState.activeIndex}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSuggestionClick(suggestion);
                      }
                    }}
                    tabIndex={0}
                  >
                    {suggestion?.address}
                  </SuggestionItem>
                ))
              )}
            </SuggestionsList>
          </SearchContainer>
        </Wrapper>

        {/* Loading indicator for zone details */}
        {isLoadingZoneDetails && (
          <div role="status" aria-live="polite">
            <IntlMessages id="loading.zone.details" defaultMessage="Loading zone details..." />
          </div>
        )}

        {isAPICalled && !formState.isAddressFind && (
          <ErrorMessage role="alert" aria-live="assertive">
            <span aria-hidden="true">⚠️</span> <IntlMessages id="address.unsupported.message" />
          </ErrorMessage>
        )}

        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || isLoadingZoneDetails}
          aria-label={
            isLoadingZoneDetails ? 'Loading zone details, please wait' : 'Proceed to map view'
          }
        >
          {isLoadingZoneDetails ? (
            <IntlMessages id="loading.button" defaultMessage="Loading..." />
          ) : (
            <IntlMessages id="button.label" defaultMessage="Next" />
          )}
        </Button>
      </SectionLeft>
      <SectionRight aria-hidden="true" bg={addressSearchBg} />
    </Main>
  );
};

export default AdminAddressSearch;
